import themeVariables from "@/config/theme/themeVariable";
import { Button, Modal } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useSearchParams } from "next/navigation";
import { useEffect, useState, useMemo } from "react";

// 타입 정의
interface LineDetail {
  line_strength: string;
  recommended_method: string;
  suitable_face: string;
  caution_cases: string;
  expected_changes: string;
  celebrity_style: string;
  cost: string;
  cost_reason: string;
}

interface LineType {
  id: string;
  label: string;
  name: string;
  description: string;
  suited: string;
  image: string;
  celebrityImage?: string;
  celebrityName?: string;
  pros: string[];
  cons: string[];
  details: LineDetail;
}

interface EyesLinesModalProps {
  forceOpened?: boolean;
  onClose?: () => void;
}

// 상수 데이터 분리
const LINE_TYPES: LineType[] = [
  {
    id: 'inline',
    label: '인라인 (In-line)',
    name: "인라인 (In-line)",
    description: "눈의 안쪽에서 시작하여 눈꺼풀 안쪽 가까이에 위치하는 라인입니다. 자연스럽고 은은한 쌍꺼풀을 만듭니다.",
    suited: "자연스러운 쌍꺼풀을 원하는 사람, 눈꺼풀이 두꺼운 사람, 선천적인 쌍꺼풀 효과를 원하는 사람",
    image: "https://ucarecdn.com/944e6342-2c6d-449c-b621-809acc5d81d3/inline.png",
    celebrityImage: "https://ucarecdn.com/6b3eb99d-7db1-4bf2-9d3b-14e7c27d3b5c/noroot.png",
    celebrityName: "블랙핑크 지수 (Jisoo)",
    pros: [
      "자연스러운 결과",
      "부자연스러운 인상을 피할 수 있음",
      "선천적인 쌍꺼풀처럼 보임",
      "얇은 라인으로 인한 부담 없는 눈매",
      "티가 별로 나지 않아 자연스러운 라인 생성 가능",
      "경력이 많지 않은 의사도 시술하기 용이한 편"
    ],
    cons: [
      "뚜렷한 인상을 원할 경우 적합하지 않음",
      "라인이 옅어 눈이 여전히 작아 보일 수 있음",
      "눈화장 시 효과가 덜할 수 있음",
      "라인이 잘 보이지 않을 수 있음",
      "눈 뜨기가 편해지지 않을 수 있음"
    ],
    details: {
      line_strength: "★★★★☆ (시간 지나며 다소 자연스럽게 풀릴 수 있음)",
      recommended_method: "비절개법, 부분절개법",
      suitable_face: "계란형 얼굴 / 몽고주름 적은 눈",
      caution_cases: "눈이 너무 작거나 몽고주름 심하면 부자연스러워질 수 있음",
      expected_changes: "2~3개월 이후 라인 부드러워지고 자연스러워짐",
      celebrity_style: "블랙핑크 지수 (Jisoo) 스타일처럼 자연스러운 쌍꺼풀",
      cost: "150~250만 원",
      cost_reason: "자연스러운 얇은 라인 → 비절개법이 주로 사용되어 수술 난이도가 상대적으로 낮음"
    }
  },
  {
    id: 'outline',
    label: '아웃라인 (Out-line)',
    name: "아웃라인 (Out-line)",
    description: "눈꺼풀 가장자리에 위치하는 라인으로, 뚜렷하고 또렷한 쌍꺼풀을 만듭니다. 서양인과 같은 깊은 이중 라인을 형성합니다.",
    suited: "뚜렷한 쌍꺼풀을 원하는 사람, 눈이 작아 보이는 사람, 큰 눈 효과를 원하는 사람",
    image: "https://ucarecdn.com/b34f6302-bb37-424e-b14d-21870dbe2594/outlline.png",
    celebrityImage: "https://ucarecdn.com/38219409-0eff-4131-a680-09f273fa23be/noroot.png",
    celebrityName: "소녀시대 윤아 (Yoona)",
    pros: [
      "눈이 크고 또렷해 보이는 효과",
      "눈화장 시 눈매가 잘 강조됨",
      "확실한 이중 라인 형성",
      "뚜렷한 인상 변화",
      "확실한 라인이 만들어져서 또렷한 눈매 형성 가능",
      "확실히 눈 뜨는 힘이 좋아짐",
      "피곤하고 잠 온 듯한 눈매 개선"
    ],
    cons: [
      "인공적으로 보일 가능성",
      "동양인 얼굴과 부조화될 수 있음",
      "시간이 지나면서 눈꺼풀이 무거워 보일 수 있음",
      "라인이 실 때 움직이는 티가 심해짐",
      "흉터의 가능성이 높을 수 있음",
      "회복기가 길고 부기가 더 심할 수 있음"
    ],
    details: {
      line_strength: "★★★★☆ (두꺼운 라인이 잘 유지되나 노화로 풀릴 수도 있음)",
      recommended_method: "절개법 (확실한 고정)",
      suitable_face: "각진 얼굴형 / 눈두덩이 얇고 길쭉한 눈",
      caution_cases: "눈이 작은 경우 과장된 느낌 가능성 있음",
      expected_changes: "초기에 부기 심하지만 3~6개월 후 강한 인상 완성",
      celebrity_style: "소녀시대 윤아 스타일처럼 또렷한 쌍꺼풀",
      cost: "180~300만 원",
      cost_reason: "선명하고 높은 라인 → 절개법 필수 + 피부 및 근육 고정 작업 추가 필요"
    }
  },
  {
    id: 'inoutline',
    label: '인아웃라인 (In/Out-line)',
    name: "인아웃라인 (In/Out-line)",
    description: "눈의 안쪽은 인라인으로, 바깥쪽은 아웃라인으로 처리하는 혼합형 라인입니다. 자연스러움과 또렷함을 동시에 갖춘 균형 잡힌 쌍꺼풀을 만듭니다.",
    suited: "자연스러우면서도 또렷한 눈매를 원하는 사람, 균형 잡힌 이목구비를 가진 사람",
    image: "https://ucarecdn.com/f58ad919-bfe6-44db-a777-15bacc338825/inoutline.png",
    celebrityImage: "https://ucarecdn.com/6b92e0c9-ba86-46b5-8a90-9a788103471a/noroot.png",
    celebrityName: "트와이스 쯔위 (Tzuyu)",
    pros: [
      "자연스러움과 또렷함 모두 가능",
      "동양인 얼굴형에 적합",
      "눈의 형태에 따라 맞춤형 디자인 가능",
      "적절한 눈매 강조 효과",
      "결막에서 시작하여 피부로 연결되는 자연스러운 흐름",
      "자연스러운 평행형 라인 표현 가능",
      "과하지 않은 쌍꺼풀이 가능"
    ],
    cons: [
      "시술이 복잡해질 수 있음",
      "의사의 숙련도에 따라 결과 차이가 큼",
      "비대칭 가능성이 있음",
      "라인과 눈의 형태에 대한 이해가 필요함",
      "시간이 지나면서 변형이 생길 수 있음"
    ],
    details: {
      line_strength: "★★★★★ (자연스럽게 오래 유지)",
      recommended_method: "비절개법 or 절개법 (눈 상태에 따라)",
      suitable_face: "둥근 얼굴형, 긴 얼굴형 / 대체로 모든 눈형 가능",
      caution_cases: "앞트임 병행 안 하면 앞부분 어색할 수 있음",
      expected_changes: "2~4개월 후 가장 자연스럽게 자리 잡음",
      celebrity_style: "트와이스 쯔위 스타일처럼 자연+또렷 조합",
      cost: "180~300만 원",
      cost_reason: "복합 라인 (인라인+아웃라인 느낌) → 절개 또는 비절개 선택 가능하지만 디자인 난이도 높음"
    }
  },
  {
    id: 'semioutline',
    label: '세미아웃라인 (Semi Out-line)',
    name: "세미아웃라인 (Semi Out-line)",
    description: "아웃라인보다는 덜 뚜렷하지만 인라인보다는 더 또렷한 중간 정도의 라인입니다. 자연스러움과 또렷함의 균형을 맞춘 형태입니다.",
    suited: "너무 뚜렷하지 않으면서도 분명한 쌍꺼풀을 원하는 사람, 비교적 얇은 눈꺼풀을 가진 사람",
    image: "https://ucarecdn.com/92d3cec9-d042-4f5a-8622-5f17808609e5/semioutline.png",
    celebrityImage: "https://ucarecdn.com/409a153f-7815-458d-b706-c22c2b1d3353/noroot.png",
    celebrityName: "아이유 (IU)",
    pros: [
      "균형 잡힌 눈매",
      "인공적이지 않고 적당히 또렷함",
      "대부분의 얼굴형에 어울림",
      "화장 시 효과가 좋음",
      "적당한 위치에 라인을 만들어서 자연스럽고 또렷함",
      "동양인이 가진 눈의 특징을 해치지 않는 선에서 쌍꺼풀 표현",
      "풀 메이크업 시 눈화장이 가려지지 않음"
    ],
    cons: [
      "특별히 뚜렷한 변화를 원할 경우 부족함",
      "눈꺼풀이 두꺼운 경우 효과가 덜할 수 있음",
      "의사의 기술에 따라 결과 차이가 있음",
      "과한 기대를 하면 아쉬울 수 있음",
      "경미한 수정 수술이 필요할 수 있음"
    ],
    details: {
      line_strength: "★★★★☆ (자연스럽지만 약간 낮아질 수 있음)",
      recommended_method: "비절개법, 절개법 (눈두덩이 지방 많을 경우 절개)",
      suitable_face: "갸름한 얼굴형 / 눈꼬리가 살짝 올라간 눈",
      caution_cases: "눈 위에 지방이 너무 많으면 흐려질 수 있음",
      expected_changes: "2~3개월 후 부드럽고 자연스럽게 자리 잡음",
      celebrity_style: "아이유(IU) 스타일처럼 은은한 쌍꺼풀",
      cost: "150~270만 원",
      cost_reason: "자연스러움과 선명함의 중간 → 케이스에 따라 절개법 병행 필요로 난이도 차이 발생"
    }
  },
  {
    id: 'hiddenline',
    label: '히든라인 (Hidden Line)',
    name: "히든라인 (Hidden Line)",
    description: "눈을 떴을 때는 쌍꺼풀이 거의 보이지 않지만, 눈을 부분적으로 감았을 때 은은하게 드러나는 라인입니다. 매우 자연스러운 효과를 줍니다.",
    suited: "최대한 자연스러운 결과를 원하는 사람, 속쌍꺼풀이 있거나 선천적인 쌍꺼풀과 유사한 효과를 원하는 사람",
    image: "https://ucarecdn.com/99c8ddb7-5fd7-4d6c-9cb2-69f2579c60d0/hiddenline.png",
    celebrityImage: "https://ucarecdn.com/40ff2eba-36a6-47fa-9a4c-454705bbf2c3/noroot.png",
    celebrityName: "박보영 (Park Bo-young)",
    pros: [
      "극도로 자연스러운 결과",
      "선천적 쌍꺼풀과 유사한 효과",
      "수술한 티가 거의 나지 않음",
      "표정 변화에 따라 은은하게 드러나는 라인",
      "눈을 떴을 때 거의 표시 나지 않음",
      "인위적인 느낌이 전혀 없어 자연스러움",
      "회복 기간이 짧고 붓기가 적음"
    ],
    cons: [
      "뚜렷한 변화를 원할 경우 적합하지 않음",
      "효과가 미미하게 느껴질 수 있음",
      "시간이 지나면서 라인이 흐려질 가능성",
      "표시가 거의 없어 수술 효과에 실망할 수 있음",
      "비용 대비 효과가 적다고 느낄 수 있음",
      "선천적 쌍꺼풀이 없는 사람의 경우 효과가 크지 않을 수 있음"
    ],
    details: {
      line_strength: "★★★☆☆ (자연스러우나 풀릴 위험 존재)",
      recommended_method: "비절개법 강추 (흉터 최소화)",
      suitable_face: "모든 얼굴형 가능 / 피부 얇고 탄력 좋은 눈",
      caution_cases: "지방 많거나 탄력 약한 눈은 풀릴 확률 높음",
      expected_changes: "1~2개월 후 라인 거의 안 보이며 자연스러움",
      celebrity_style: "박보영 스타일처럼 '수술한 티 안나는' 자연 눈매",
      cost: "130~220만 원",
      cost_reason: "라인이 거의 안 보이는 자연스러운 결과 → 비절개법 중심, 흉터 최소화 기술 필요하나 난이도는 비교적 낮음"
    }
  }
];

// 재사용 가능한 UI 컴포넌트
const ImageSection = ({ line }: { line: LineType }) => (
  <div className="flex flex-col items-center mb-6">
    <div className="flex flex-col md:flex-row gap-4 w-full justify-center items-start">
      <div className="flex-1 max-w-[300px]">
        <img 
          src={line.image} 
          alt={`${line.name} 설명 이미지`} 
          className="rounded-md w-full border border-gray-200 dark:border-gray-700"
          style={{ maxHeight: '220px', objectFit: 'cover' }}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
          쌍꺼풀 라인 예시
        </p>
      </div>
      
      {line.celebrityImage && (
        <div className="flex-1 max-w-[300px]">
          <img 
            src={line.celebrityImage} 
            alt={`${line.celebrityName} 이미지`} 
            className="rounded-md w-full border border-gray-200 dark:border-gray-700"
            style={{ 
              height: '220px', 
              objectFit: 'cover',
              objectPosition: line.id === 'inoutline' ? '0 10%' : line.id === 'hiddenline' ? '0 20%' : 'center'
            }}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
            {line.celebrityName}
          </p>
        </div>
      )}
    </div>
  </div>
);

const ProsConsSection = ({ pros, cons }: { pros: string[], cons: string[] }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
    <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
      <h3 className="text-sm font-medium text-green-700 dark:text-green-400 mb-2 text-center">장점</h3>
      <ul className="list-none space-y-1 text-gray-600 dark:text-gray-400 text-center mx-auto w-fit">
        {pros.map((pro, idx) => (
          <li key={idx}>{pro}</li>
        ))}
      </ul>
    </div>
    
    <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
      <h3 className="text-sm font-medium text-red-700 dark:text-red-400 mb-2 text-center">단점</h3>
      <ul className="list-none space-y-1 text-gray-600 dark:text-gray-400 text-center mx-auto w-fit">
        {cons.map((con, idx) => (
          <li key={idx}>{con}</li>
        ))}
      </ul>
    </div>
  </div>
);

const DetailTable = ({ details }: { details: LineDetail }) => (
  <div className="overflow-hidden bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
        <tr>
          <td className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800/80 w-1/3">라인 유지력</td>
          <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400">{details.line_strength}</td>
        </tr>
        <tr>
          <td className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800/80">추천 수술 방법</td>
          <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400">{details.recommended_method}</td>
        </tr>
        <tr>
          <td className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800/80">어울리는 얼굴형/눈 형태</td>
          <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400">{details.suitable_face}</td>
        </tr>
        <tr>
          <td className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800/80">주의해야 할 케이스</td>
          <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400">{details.caution_cases}</td>
        </tr>
        <tr>
          <td className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800/80">수술 후 변화 예상</td>
          <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400">{details.expected_changes}</td>
        </tr>
        <tr>
          <td className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800/80">대표 유명인 스타일</td>
          <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400">{details.celebrity_style}</td>
        </tr>
        <tr className="bg-blue-50 dark:bg-blue-900/20">
          <td className="px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-400">예상 비용</td>
          <td className="px-3 py-2 text-sm text-blue-600 dark:text-blue-300 font-medium">{details.cost}</td>
        </tr>
        <tr className="bg-blue-50 dark:bg-blue-900/20">
          <td className="px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-400">비용 차이 이유</td>
          <td className="px-3 py-2 text-sm text-blue-600 dark:text-blue-300">{details.cost_reason}</td>
        </tr>
      </tbody>
    </table>
  </div>
);

export const EyesLinesModal = ({ forceOpened = false, onClose }: EyesLinesModalProps) => {
  const [opened, { open, close }] = useDisclosure(forceOpened);
  const searchParams = useSearchParams();
  const [activeLine, setActiveLine] = useState<string | null>(null);
  
  useEffect(() => {
    if (searchParams?.get('modal') === 'eyesLines' || forceOpened) {
      open();
      // 모달이 열릴 때 자동으로 첫번째 라인 유형 선택
      setActiveLine('inline');
    }
  }, [searchParams, open, forceOpened]);

  const handleLineClick = (id: string) => {
    setActiveLine(id === activeLine ? null : id);
  };
  
  // 성능 최적화를 위해 현재 활성화된 라인 정보를 useMemo로 캐싱
  const activeLineData = useMemo(() => {
    if (!activeLine) return null;
    return LINE_TYPES.find(t => t.id === activeLine);
  }, [activeLine]);
  
  const renderLineContent = () => {
    if (!activeLineData) return null;
    
    return (
      <div className="mt-4 animate-fadeIn">
        <h2 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100 text-center">
          {activeLineData.name}
        </h2>
        
        <ImageSection line={activeLineData} />
        
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 text-center">
          {activeLineData.description}
        </p>
        
        <ProsConsSection pros={activeLineData.pros} cons={activeLineData.cons} />
        
        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg mb-4">
          <h4 className="text-sm font-medium text-blue-700 dark:text-blue-400 mb-2 text-center">적합한 사람</h4>
          <p className="text-gray-600 dark:text-gray-400 text-center">{activeLineData.suited}</p>
        </div>

        <DetailTable details={activeLineData.details} />
      </div>
    );
  };
  
  return (
    <Modal
      opened={opened}
      onClose={() => {
        close();
        if (onClose) onClose();
      }}
      withCloseButton={false}
      centered
      radius={'lg'}
      padding={24}
      size="lg"
      closeOnClickOutside={true}
      classNames={{ 
        content: 'scrollbar-hide max-w-full md:max-w-[700px]', 
        overlay: 'cursor-pointer' 
      }}
    >
      <div className="flex flex-col gap-3 mb-5">
        <div className="text-lg text-gray-700 dark:text-gray-100 font-semibold" data-autofocus>
          쌍꺼풀 라인의 종류
        </div>
        <p className="text-[15px] text-gray-700 dark:text-gray-100 font-normal">
          쌍꺼풀 라인은 형태, 굵기, 위치에 따라 다양한 모양이 있으며, 얼굴형과 눈의 특성에 맞게 선택해야 합니다.
        </p>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 max-w-[560px] mx-auto">
        {LINE_TYPES.map((line, index) => (
          <button
            key={index}
            className={`border border-gray-200 dark:border-gray-700 p-4 text-center h-[76px] text-gray-700 text-[15px] font-normal hover:border-blue-700 hover:text-blue-700 dark:hover:text-blue-400 transition-colors cursor-pointer rounded-lg bg-white dark:bg-gray-800 flex flex-col items-center justify-center ${activeLine === line.id 
              ? 'border-blue-700 text-blue-700 dark:text-blue-400' 
              : ''}`}
            onClick={() => handleLineClick(line.id)}
          >
            <div className="text-[15px] font-normal">{line.label}</div>
          </button>
        ))}
      </div>
      
      {activeLine && renderLineContent()}
      
      <div className="flex gap-1 justify-end mt-5">
        <Button 
          classNames={themeVariables.button.gray} 
          onClick={() => {
            close();
            if (onClose) onClose();
          }}
        >
          닫기
        </Button>
      </div>
    </Modal>
  );
}